import type { Enabler, Objective, Resource, ResourceFormat } from "../types";
import { HighlightedText } from "./HighlightedText";
import { ExternalIcon, FormatIcon, MedalIcon } from "./Icons";

const FORMAT_CLASS: Record<ResourceFormat, string> = {
  Video: "resource-card__badge resource-card__badge--video",
  Article: "resource-card__badge resource-card__badge--article",
  Podcast: "resource-card__badge resource-card__badge--podcast",
  Book: "resource-card__badge resource-card__badge--book",
  "PDF/Guide": "resource-card__badge resource-card__badge--guide",
  Research: "resource-card__badge resource-card__badge--research",
  Tutorial: "resource-card__badge resource-card__badge--tutorial",
  Interview: "resource-card__badge resource-card__badge--interview",
  Other: "resource-card__badge resource-card__badge--other"
};

type ResourceCardProps = {
  resource: Resource;
  enablers?: Enabler[];
  objectives?: Objective[];
  searchTerm?: string;
  isTopRecommended?: boolean;
  recommendationTier?: "gold" | "silver" | "bronze";
};

export function ResourceCard({
  resource,
  enablers = [],
  objectives = [],
  searchTerm = "",
  isTopRecommended = false,
  recommendationTier = "gold"
}: ResourceCardProps) {
  return (
    <a
      className={isTopRecommended ? "resource-card resource-card--top-suggested" : "resource-card"}
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span
        className={
          isTopRecommended
            ? `resource-card__medal resource-card__medal--${recommendationTier}`
            : FORMAT_CLASS[resource.format]
        }
      >
        {isTopRecommended ? (
          <>
            <MedalIcon />
            <span>Top Suggested Resource</span>
          </>
        ) : (
          <FormatIcon />
        )}
      </span>
      <span className="resource-card__body">
        <span className="resource-card__heading">
          <span className="resource-card__title">
            <HighlightedText text={resource.name} searchTerm={searchTerm} />
          </span>
          <ExternalIcon className="resource-card__external" />
        </span>
        {resource.summary ? (
          <span className="resource-card__summary">
            <HighlightedText text={resource.summary} searchTerm={searchTerm} />
          </span>
        ) : null}
        <span className="resource-card__meta-row">
          <span className="resource-card__format">{resource.format}</span>
          {objectives.map((objective) => (
            <span className="resource-card__tag" key={objective.id}>
              {objective.code}
            </span>
          ))}
        </span>
        {enablers.length > 0 ? (
          <span className="resource-card__tags" aria-label="Enablers">
            {enablers.map((enabler) => (
              <span className="resource-card__tag resource-card__tag--enabler" key={enabler.code}>
                {enabler.code} {enabler.title}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </a>
  );
}
